import { FC, useEffect } from "react";
import styled from "styled-components/macro";
import { motion } from "framer-motion";
import GitHubIcon from "@mui/icons-material/GitHub";
import {
  StyledPageWrapper,
  ContentWrapper,
  PageTitle,
  Divider,
  IntroText,
  ItalicNote,
  Paragraph,
  SectionTitleH2,
  ProjectDescription,
  ProjectSubtitle,
  ProjectTitle,
  ProjectContainer,
  ProjectHeader,
} from "./StyledComponents";
import { StyledTooltip } from "./StyledTooltip";
import { techList } from "./data/data";
import BackButton from "./BackButton";

interface Project {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  technologies: string[];
  image: string;
  githubUrl: string;
  liveUrl: string;
  featured: boolean;
  category: string;
}

const projectsData: Project[] = [
  {
    id: 1,
    title: "Real-Time Micro-Frontend Trading Platform",
    subtitle: "Full-Stack React Application",
    description:
      "A modular financial trading platform built using a micro-frontend architecture and Module Federation, designed to deliver a unified, real-time trading experience through independently deployed frontend applications. The system features a shell container that dynamically loads multiple MFEs, including a trading interface with live market charts, a trade blotter, and a portfolio management module currently in development. Market data is streamed real-time via WebSockets using Protocol Buffers, enabling efficient binary serialization and continuous live updates. Interactive candlestick charts, live instrument grids, and real-time position monitoring showcase how complex trading functionality can be composed from decoupled, independently evolving frontend systems.",
    technologies: [
      "TypeScript",
      "React",
      "Redux",
      "RxJS",
      "XState",
      "Vite",
      "styled-components",
      "Ag-Grid",
      "Figma",
    ],
    image: "tradeapp.gif",
    githubUrl: "https://github.com/XandraV/real-time-streaming-mfe",
    liveUrl: "#",
    featured: true,
    category: "Data Visualization",
  },
  {
    id: 2,
    title: "Minerapedia - Interactive Mineral Database",
    subtitle: "React & D3.js Data Visualisation Application",
    description:
      "A React application that allows users to discover and explore minerals by interacting directly with the periodic table. Users can select chemical elements to perform mineral searches, browse detailed mineral profiles, and analyze mineral data through interactive dashboards. The app integrates real mineral data from the Mindat database and presents it through heatmaps, sunburst charts, and bubble charts. Each mineral has a dedicated profile page showcasing its chemical composition, physical properties, crystal structure information, and interactive 3D visualizations built with Three.js. Designed for mineral enthusiasts!",
    technologies: [
      "React",
      "TypeScript",
      "D3.js",
      "Redux",
      "styled-components",
      "AWS",
    ],
    image: "mineraldemo.gif",
    githubUrl: "https://github.com/XandraV/minerals-ui",
    liveUrl: "https://main.d3i0kpvbqkkgy8.amplifyapp.com/",
    featured: true,
    category: "Full-Stack Development",
  },
];

export const SectionTitle = styled.div`
  font-size: 28px;
  text-align: left;
  font-family: EB Garamond, serif;
`;

const TechIconWrapper = styled.div`
  svg {
    width: 40px;
    height: 30px;
  }
`;

const TechStackContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  margin-bottom: 25px;
  flex-wrap: wrap;
  gap: 8px;
`;

const ProjectImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 2px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
`;

const GitHubButton = styled.a`
  width: 140px;
  align-items: center;
  gap: 8px;
  color: #7fa9e3ff;
  background: none;
  border: 1px solid #7fa9e3ff;
  border-radius: 30px;
  padding: 8px 16px;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  font-family: poppins, sans-serif;
  &:hover {
    color: #5a9bf7;
    border-color: #5a9bf7;
    background-color: rgba(127, 169, 227, 0.1);
    transform: translateY(-2px);
  }

  svg {
    width: 22px;
    height: 22px;
  }
`;

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    width: "90%",
    transition: {
      staggerChildren: 0.22,
      delayChildren: 0.15,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

const fadeUp = {
  hidden: {
    opacity: 0,
    y: 40,
    scale: 0.98,
  },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};
const ProjectsPage: FC<{ title?: string; projects?: Project[] }> = ({
  title = "Projects",
  projects = projectsData,
}) => {
  useEffect(() => {
    window.scroll({
      top: 0,
      behavior: "smooth",
    });
  });
  return (
    <motion.div
      key={title}
      variants={containerVariants}
      initial="hidden"
      animate="show"
    >
      <StyledPageWrapper>
        <ContentWrapper>
          <BackButton to="/portfolio" title="Back to Portfolio" />
          <PageTitle>{title}</PageTitle>
          <Divider />
          <IntroText>
            A collection of projects that reflect my passion for frontend
            engineering and visual storytelling. These works showcase technical
            depth, creativity, and a focus on building engaging digital
            experiences. Each project represents a unique challenge and learning
            opportunity.
          </IntroText>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="show"
          >
            {projects.map((project, index) => (
              <motion.div key={project.id} variants={fadeUp}>
                <ProjectContainer>
                  <ProjectHeader>
                    <ProjectTitle>{project.title}</ProjectTitle>
                    <ProjectSubtitle>{project.subtitle}</ProjectSubtitle>
                  </ProjectHeader>
                  <ProjectDescription>{project.description}</ProjectDescription>
                  <TechStackContainer>
                    <div style={{ display: "flex" }}>
                      {project.technologies.map((tech: string) => {
                        const techDetails = techList.find(
                          (t) => t.name === tech
                        );
                        return (
                          <TechIconWrapper key={tech}>
                            <svg width={40} height={30}>
                              <StyledTooltip
                                title={techDetails?.name ?? tech}
                                aria-label={techDetails?.name ?? tech}
                                placement="bottom"
                                arrow
                              >
                                <image
                                  x={0}
                                  y={0}
                                  width={30}
                                  height={30}
                                  href={techDetails?.link}
                                />
                              </StyledTooltip>
                            </svg>
                          </TechIconWrapper>
                        );
                      })}
                    </div>
                    {project.githubUrl && project.githubUrl !== "#" && (
                      <GitHubButton
                        href={project.githubUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <GitHubIcon />
                        View on GitHub
                      </GitHubButton>
                    )}
                  </TechStackContainer>
                  {/* <ProjectImage
                    src={`https://general-gif-bucket.s3.eu-west-2.amazonaws.com/${project.image}`}
                    alt={`${project.title} screenshot`}
                  /> */}
                </ProjectContainer>

                {index < projects.length - 1 && <Divider width={60} />}
              </motion.div>
            ))}
          </motion.div>
          <SectionTitleH2>Project Philosophy</SectionTitleH2>
          <Paragraph>
            Each project is a space for experimentation and learning. I enjoy
            turning complex ideas and data into interactive experiences and I’m
            especially drawn to visualisation-heavy interfaces where frontend
            technology meets creative design and art. These projects reflect my
            curiosity and love for building things that invite exploration and
            spark interest.
          </Paragraph>

          <ItalicNote alignRight>
            More projects are currently in development and will be added to this
            portfolio soon.
          </ItalicNote>
        </ContentWrapper>
      </StyledPageWrapper>
    </motion.div>
  );
};

export default ProjectsPage;
